import { inject, Injectable } from '@angular/core';
import {
  BaseWebsocketMessage,
  SubWebsocketMessageRequest,
  WebsocketEntTypes,
  WebSocketService,
  WSEntityManager,
} from '@qtek/core/websockets-core';
import { Observable } from 'rxjs';

export const WORKFLOW_TASKS_MYSID = 'workflow_tasks';

export interface WorkflowTaskDependency {
  prerequisiteId: string;
  dependentId?: string;
  condition: string;
}

export interface WorkflowTaskTrigger {
  _id: string;
}

export interface WorkflowTaskConfigurationPayload {
  _id?: string;
  ownerType: 'sys' | 'prs';
  status: string;
  action: string;
  refId: string;
  params: Record<string, any>;
  executeDate: string | null;
  usrId?: string;
  workflowId: string;
  lastInWorkflow?: boolean;
  dependencies?: WorkflowTaskDependency[];
  triggers?: WorkflowTaskTrigger[];
}

@Injectable({
  providedIn: 'root',
})
export class WorkflowTaskConfigurationService {
  private readonly webSocketService = inject(WebSocketService);

  entityName: WebsocketEntTypes = 'workflowTaskConfiguration';
  wsManager = new WSEntityManager(this.webSocketService, this.entityName);

  readonly webSocketIsAlive$ = this.webSocketService.getWebSocketIsAlive$();

  /**
   * Subscribe to workflow task configuration updates
   */
  subscribe(
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'> = {}
  ): Observable<BaseWebsocketMessage> {
    return this.wsManager.subscribeEntity({
      ...payload,
      mysid: payload.mysid || WORKFLOW_TASKS_MYSID,
    });
  }

  /**
   * Create a new workflow task configuration (ins operation)
   * @param payload - Task configuration data
   */
  createTask(payload: WorkflowTaskConfigurationPayload): void {
    this.wsManager.create({
      res: payload,
      mysid: WORKFLOW_TASKS_MYSID,
    });
  }

  /**
   * Update an existing workflow task configuration (upd operation)
   * @param payload - Partial task configuration data to update
   * @param id - Task ID
   */
  updateTask(
    payload: Partial<WorkflowTaskConfigurationPayload>,
    id: string
  ): void {
    this.wsManager.update({
      res: payload,
      id,
      mysid: WORKFLOW_TASKS_MYSID,
    });
  }

  /**
   * Swap two workflow tasks (upds operation for reordering)
   * @param taskId1 - First task ID
   * @param taskId2 - Second task ID
   */
  swapTasks(taskId1: string, taskId2: string): void {
    this.wsManager.bulkUpdate({
      res: [{ _id: taskId1 }, { _id: taskId2 }],
      mysid: WORKFLOW_TASKS_MYSID,
    });
  }

  /**
   * Delete a workflow task configuration (del operation)
   * @param id - Task ID
   * @param workflowId - Workflow ID the task belongs to
   */
  deleteTask(id: string, workflowId?: string): void {
    this.wsManager.delete({
      id,
      ...(workflowId ? { workflowId } : {}),
      mysid: WORKFLOW_TASKS_MYSID,
    });
  }

  /**
   * Query workflow task configurations
   * @param payload - Query parameters
   */
  queryTasks(payload: any = {}): void {
    this.wsManager.query({
      ...payload,
      mysid: WORKFLOW_TASKS_MYSID,
    });
  }

  /**
   * Get a specific workflow task configuration
   * @param id - Task ID
   */
  getTask(id: string): void {
    this.wsManager.get({
      id,
      mysid: WORKFLOW_TASKS_MYSID,
    });
  }
}
